import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import $ from "jquery";
import "select2/dist/css/select2.min.css";
import "select2";
import Select2Dropdown from "../components/common/Select2Dropdown";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import { masterDataAPI, approvalWorkflowAPI, categoryMasterAPI } from "../services/api";
import "./DynamicApprovalConfigurationLevelSetup.css";
import "./RaiseComplaint.css";
import "./UserManagement.css";
import "./AddEditUser.css";
import "./DynamicApprovalConfiguration.css";

/** Parse WorkflowSource JSON for user specification (same as Approval Level Setup). */
const parseUserSpecificationFromWorkflowSource = (raw) => {
  if (typeof raw !== "string" || !raw.trim().startsWith("{")) return "None";
  try {
    const o = JSON.parse(raw);
    const s = o.userSpecificationType ?? o.UserSpecificationType;
    if (s == null || String(s).trim() === "") return "None";
    const t = String(s).trim();
    const norm = t === "LogHead" ? "CPD" : t;
    return ["None", "Plant", "RDC", "BCM", "CPD", "General"].includes(norm) ? norm : "None";
  } catch {
    return "None";
  }
};

/** Domestic (M001) vs Export (M002) from market code / name. */
const resolveMarketKind = (marketCode, marketName) => {
  const c = String(marketCode || "").trim().toUpperCase();
  const n = String(marketName || "").trim().toUpperCase();
  if (c === "M001" || n === "DOMESTIC" || c.includes("DOMESTIC") || (c.includes("DOM") && !c.includes("EXP"))) {
    return "Domestic";
  }
  if (c === "M002" || n === "EXPORT" || c.includes("EXPORT") || c.includes("EXP")) {
    return "Export";
  }
  return "";
};

const USER_SPEC_OPTIONS_DOMESTIC = [
  { id: "Plant", text: "Plant" },
  { id: "RDC", text: "RDC" },
  { id: "BCM", text: "BCM" },
  { id: "General", text: "General" },
];

const USER_SPEC_OPTIONS_EXPORT = [
  { id: "Plant", text: "Plant" },
  { id: "CPD", text: "CPD" },
  { id: "General", text: "General" },
];

/** SR Journey Flow choices from selected market(s). */
const getUserSpecificationOptionsForMarkets = (marketCodes, marketsList) => {
  const codes = Array.isArray(marketCodes)
    ? marketCodes.map((c) => String(c).trim()).filter(Boolean)
    : [];
  if (!codes.length) return [];

  const kinds = new Set();
  for (const mc of codes) {
    const m = (marketsList || []).find(
      (x) =>
        String(x.marketCode || x.MARKETCODE || x.MarketCode || "").trim().toUpperCase() ===
        mc.toUpperCase()
    );
    const kind = resolveMarketKind(
      mc,
      m?.marketName || m?.MARKETNAME || m?.MarketName || ""
    );
    if (kind) kinds.add(kind);
  }

  if (kinds.has("Domestic") && kinds.has("Export")) {
    const byId = new Map();
    [...USER_SPEC_OPTIONS_DOMESTIC, ...USER_SPEC_OPTIONS_EXPORT].forEach((o) =>
      byId.set(o.id, o)
    );
    return [...byId.values()];
  }
  if (kinds.has("Export")) return [...USER_SPEC_OPTIONS_EXPORT];
  if (kinds.has("Domestic")) return [...USER_SPEC_OPTIONS_DOMESTIC];
  return [];
};

/**
 * DynamicApprovalValueConfigurationLevelSetup Component
 * Manages dynamic approval value configuration levels with drag-and-drop reordering,
 * row insertion/deletion, and configurable fields per row.
 */
const DynamicApprovalValueConfigurationLevelSetup = () => {
  const navigate = useNavigate();
  const { workflowCode } = useParams();
  const location = useLocation();
  // Get PageType from URL search params or default
  const searchParams = new URLSearchParams(location.search);
  const initialPageType = searchParams.get('pageType') || 'ApprovalValueConfiguration';
  
  // Edit mode: if workflowCode exists in URL params
  const isEditMode = !!workflowCode;
  // Add mode: if no workflowCode in URL params
  const isAddMode = !workflowCode;
  
  // Page Type state
  const [pageType, setPageType] = useState(initialPageType);
  
  // Workflow form states
  const [workflowForm, setWorkflowForm] = useState({
    workflowCode: "",
    workflowName: "",
    srTypeCode: "",
    marketCode: "0000",
    marketCodes: [], // single market in UI (one-element array)
    catgCodes: [],
    userSpecificationType: "",
    workflowSource: "",
    pageType: initialPageType,
    isActive: true,
  });
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [showWorkflowForm, setShowWorkflowForm] = useState(true);
  const [markets, setMarkets] = useState([]);
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [srTypeOptions, setSrTypeOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [userRoleOptions, setUserRoleOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const select2Refs = useRef({});
  const loadSRTypesSeqRef = useRef(0);

  const approvalValueUserSpecOptions = useMemo(() => {
    if (pageType !== "ApprovalValueConfiguration") return [];
    return getUserSpecificationOptionsForMarkets(workflowForm.marketCodes, markets);
  }, [pageType, workflowForm.marketCodes, markets]);

  useEffect(() => {
    if (pageType !== "ApprovalValueConfiguration") return;
    const allowed = new Set(approvalValueUserSpecOptions.map((o) => o.id));
    setWorkflowForm((prev) => {
      const current = String(prev.userSpecificationType || "").trim();
      if (!current || current === "None" || allowed.has(current)) return prev;
      return { ...prev, userSpecificationType: approvalValueUserSpecOptions[0]?.id || "" };
    });
  }, [pageType, approvalValueUserSpecOptions]);

  // Update PageType when URL search params change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlPageType = searchParams.get('pageType') || 'ApprovalValueConfiguration';
    if (urlPageType !== pageType) {
      setPageType(urlPageType);
      setWorkflowForm((prev) => ({ ...prev, pageType: urlPageType }));
    }
  }, [location.search, pageType]);

  // Load workflow data on mount
  useEffect(() => {
    loadMarkets();
    loadUserRoles(); // Load user roles early so they're available when levels load
    if (isAddMode) {
      loadNextWorkflowCode();
    }
    if (isEditMode && workflowCode) {
      loadWorkflowForEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowCode, isEditMode, isAddMode]);

  // Business verticals (CATEGORYMASTER) for selected market(s) — same pattern as Approval Level Setup
  useEffect(() => {
    const codes = workflowForm.marketCodes || [];
    if (!codes.length) {
      setBusinessVerticals([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = [];
        const seen = new Set();
        for (const marketCode of codes) {
          const catRes = await categoryMasterAPI.getPaged({
            marketCode,
            isActive: true,
            pageNumber: 1,
            pageSize: 5000,
          });
          const cats =
            catRes?.data?.Categories ||
            catRes?.data?.categories ||
            catRes?.data?.data?.Categories ||
            [];
          if (Array.isArray(cats)) {
            for (const c of cats) {
              const id = c.catgCode || c.CATGCODE || c.CatgCode || "";
              const key = String(id).trim().toUpperCase();
              if (key && !seen.has(key)) {
                seen.add(key);
                merged.push(c);
              }
            }
          }
        }
        if (!cancelled) setBusinessVerticals(merged);
      } catch (e) {
        console.error("Failed to load business verticals", e);
        if (!cancelled) setBusinessVerticals([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [(workflowForm.marketCodes || []).join("|")]);

  const loadSRTypes = async (marketCode = null) => {
    const seq = ++loadSRTypesSeqRef.current;
    try {
      const srTypesRes = await masterDataAPI.getSRTypes(marketCode, true);
      if (seq !== loadSRTypesSeqRef.current) return;
      let srTypes = [];
      if (srTypesRes?.success && srTypesRes.data != null) {
        if (Array.isArray(srTypesRes.data)) {
          srTypes = srTypesRes.data;
        } else if (Array.isArray(srTypesRes.data?.SRTypes)) {
          srTypes = srTypesRes.data.SRTypes;
        }
      } else if (srTypesRes?.data && Array.isArray(srTypesRes.data)) {
        srTypes = srTypesRes.data;
      } else if (Array.isArray(srTypesRes)) {
        srTypes = srTypesRes;
      }
      if (seq !== loadSRTypesSeqRef.current) return;
      if (srTypes.length > 0) {
        const mappedOptions = srTypes
          .map((st) => {
            const code = String(
              st.SRTypeCode || st.SRTYPECODE || st.srTypeCode || st.SrTypeCode || ""
            ).trim();
            const rawName =
              st.Name ||
              st.NAME ||
              st.name ||
              st.SRTypeName ||
              st.sRTypeName ||
              st.Description ||
              st.description ||
              "";
            const text = String(rawName).trim() || code;
            return { id: code, text };
          })
          .filter((st) => st.id);
        setSrTypeOptions(mappedOptions);
      } else {
        setSrTypeOptions([]);
      }
    } catch (error) {
      if (seq !== loadSRTypesSeqRef.current) return;
      console.error("Error loading SR Types:", error);
      setSrTypeOptions([]);
    }
  };

  useEffect(() => {
    const first = String(workflowForm.marketCodes?.[0] || "").trim();
    if (first && first !== "0000") loadSRTypes(first);
    else loadSRTypes(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(workflowForm.marketCodes || []).join("|")]);

  // Add mode: pre-select one market (active first, else first row)
  useEffect(() => {
    if (!isAddMode || !showWorkflowForm || !markets.length) return;
    const pickDefaultMarketCode = (marketList) => {
      const active =
        marketList.find((m) => (m.isActive ?? m.ISACTIVE ?? m.IsActive) === true) ||
        marketList.find((m) => (m.isActive ?? m.ISACTIVE ?? m.IsActive) === 1);
      const m = active || marketList[0];
      if (!m) return "";
      return String(m.marketCode || m.MARKETCODE || m.MarketCode || "").trim();
    };
    const code = pickDefaultMarketCode(markets);
    if (!code) return;
    setWorkflowForm((prev) => {
      if (prev.marketCodes?.length) return prev;
      return { ...prev, marketCodes: [code], marketCode: code };
    });
  }, [markets, isAddMode, showWorkflowForm]);

  // Initialize with one default row
  useEffect(() => {
    if (rows.length === 0) {
      const defaultRow = createDefaultRow();
      setRows([defaultRow]);
    }
  }, []);

  // Load user role options (mock data - can be replaced with API call)
  useEffect(() => {
    loadUserRoles();
  }, []);

  // Update rows with proper userRole values when userRoleOptions are loaded
  useEffect(() => {
    if (userRoleOptions.length > 0 && rows.length > 0 && editingWorkflow) {
      // Re-map rows to ensure userRole values match the option IDs
      const updatedRows = rows.map((row) => {
        if (row.userRole) {
          const userRoleCode = typeof row.userRole === 'string' 
            ? row.userRole 
            : (row.userRole?.id || row.userRole?.userTypeCode || row.userRole);
          
          if (userRoleCode && userRoleCode !== '' && userRoleCode !== '0000') {
            const matchingRole = userRoleOptions.find(
              role => {
                const roleId = role.id || role.userTypeCode;
                const codeStr = String(userRoleCode).trim();
                const roleIdStr = String(roleId).trim();
                return roleIdStr === codeStr || 
                       roleIdStr === codeStr.toUpperCase() ||
                       roleIdStr.toUpperCase() === codeStr.toUpperCase();
              }
            );
            if (matchingRole) {
              // Update with the correct ID format that Select2Dropdown expects
              const correctId = matchingRole.id || matchingRole.userTypeCode || userRoleCode;
              if (String(correctId) !== String(row.userRole)) {
                return { ...row, userRole: correctId };
              }
            }
          }
        }
        return row;
      });
      
      // Only update if there are actual changes
      const hasChanges = updatedRows.some((updatedRow, index) => {
        const originalRow = rows[index];
        return String(updatedRow.userRole || '') !== String(originalRow.userRole || '');
      });
      
      if (hasChanges) {
        setRows(updatedRows);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoleOptions, editingWorkflow]);

  // Load plants
  useEffect(() => {
    const loadPlants = async () => {
      try {
        const plantsRes = await masterDataAPI.getPlants();
        if (plantsRes?.success && plantsRes.data) {
          const plants = Array.isArray(plantsRes.data) ? plantsRes.data : [];
          setPlantOptions(
            plants.map((p) => ({
              id: p.plantCode || p.PlantCode || "",
              text: p.plantName || p.PlantName || "",
            }))
            .filter((p) => p.id && p.text)
          );
        }
      } catch (error) {
        console.error("Error loading plants:", error);
      }
    };
    loadPlants();
  }, []);

  // Workflow form functions
  const loadNextWorkflowCode = async () => {
    try {
      const response = await approvalWorkflowAPI.getNextCode();
      if (response?.success && response.data) {
        setWorkflowForm((prev) => ({ ...prev, workflowCode: response.data }));
      }
    } catch (error) {
      console.error("Error loading next workflow code:", error);
    }
  };

  const loadMarkets = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 500);
      if (response?.success) {
        // Check different possible response structures
        if (response.data?.data && Array.isArray(response.data.data)) {
          setMarkets(response.data.data);
        } else if (Array.isArray(response.data)) {
          setMarkets(response.data);
        } else if (response.data?.markets && Array.isArray(response.data.markets)) {
          setMarkets(response.data.markets);
        } else {
          setMarkets([]);
        }
      } else if (Array.isArray(response?.data)) {
        setMarkets(response.data);
      } else {
        setMarkets([]);
      }
    } catch (e) {
      console.error("Error loading markets:", e);
      setMarkets([]);
    }
  };

  const loadWorkflowForEdit = async () => {
    try {
      setLoading(true);
      const response = await approvalWorkflowAPI.get(workflowCode);
      if (response?.success && response.data) {
        const workflow = response.data;
        setEditingWorkflow(workflow);
        const marketCodeRaw = workflow.marketCode || workflow.MARKETCODE || "";
        const catgCode = workflow.catgCode || workflow.CATGCODE || "";
        const srTypeCode = workflow.srTypeCode || workflow.SRTYPECODE || "";
        const wfSourceRaw =
          workflow.workflowSource || workflow.WorkflowSource || workflow.WORKFLOWSOURCE || "";

        const workflowPageType =
          workflow.pageType || workflow.PageType || searchParams.get("pageType") || "ApprovalValueConfiguration";

        const marketCodesForForm =
          workflowPageType === "ApprovalValueConfiguration" && marketCodeRaw
            ? String(marketCodeRaw)
                .split(",")
                .map((s) => s.trim())
                .filter((c) => c && c !== "0000")
            : marketCodeRaw && marketCodeRaw !== "0000"
              ? [String(marketCodeRaw).trim()]
              : [];
        const marketCodeSingle =
          workflowPageType === "ApprovalValueConfiguration"
            ? marketCodesForForm[0] || "0000"
            : marketCodeRaw || "0000";

        const catgCodesArray =
          catgCode && catgCode !== "0000" && catgCode !== "" && catgCode !== null
            ? catgCode.split(",").map((c) => c.trim()).filter((c) => c)
            : [];

        let srTypeCodeString =
          srTypeCode && srTypeCode !== "0000" && srTypeCode !== "" && srTypeCode !== null
            ? String(srTypeCode).trim()
            : "";
        if (
          workflowPageType === "ApprovalValueConfiguration" &&
          srTypeCodeString &&
          srTypeCodeString.includes(",")
        ) {
          srTypeCodeString = srTypeCodeString.split(",")[0].trim();
        }

        const userSpecFromSource = parseUserSpecificationFromWorkflowSource(wfSourceRaw);

        if (userRoleOptions.length === 0) {
          await loadUserRoles();
        }

        if (marketCodeSingle && marketCodeSingle !== "0000") {
          await loadSRTypes(marketCodeSingle);
        }
        await new Promise((resolve) => setTimeout(resolve, 150));

        setWorkflowForm({
          workflowCode: workflow.workflowCode || workflow.WorkflowCode || "",
          workflowName: workflow.workflowName || workflow.WorkflowName || "",
          srTypeCode: srTypeCodeString,
          marketCode: marketCodeSingle || "0000",
          marketCodes:
            workflowPageType === "ApprovalValueConfiguration"
              ? marketCodesForForm.length
                ? [marketCodesForForm[0]]
                : []
              : marketCodesForForm,
          catgCodes: catgCodesArray,
          userSpecificationType: userSpecFromSource,
          workflowSource: wfSourceRaw,
          pageType: workflowPageType,
          isActive:
            workflow.isActive !== undefined
              ? workflow.isActive
              : workflow.IsActive !== undefined
                ? workflow.IsActive
                : true,
        });

        setPageType(workflowPageType);

        // Load levels for this workflow (after user roles are loaded)
        await loadWorkflowLevels(workflowCode);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load workflow details",
        });
        navigate("/dynamic-approval-configuration");
      }
    } catch (error) {
      console.error("Error loading workflow:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load workflow details",
      });
      navigate("/dynamic-approval-configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowLevels = async (workflowCode) => {
    try {
      const response = await approvalWorkflowAPI.getLevels(workflowCode);
      if (response?.success && response.data) {
        const levels = Array.isArray(response.data) ? response.data : [];
        
        // Wait a bit to ensure userRoleOptions are loaded, then map rows
        const mapRowsWithRoles = () => {
          // First, map all rows without validation
          const mappedRows = levels.map((level, index) => {
            const userRoleCode = level.usertypecode || level.userTypeCode || level.USERTYPECODE || null;
            
            // Find the matching user role object if code exists and userRoleOptions are loaded
            let userRoleValue = userRoleCode;
            if (userRoleCode && userRoleOptions.length > 0) {
              const matchingRole = userRoleOptions.find(
                role => {
                  const roleId = role.id || role.userTypeCode;
                  const codeStr = String(userRoleCode).trim();
                  const roleIdStr = String(roleId).trim();
                  return roleIdStr === codeStr || 
                         roleIdStr === codeStr.toUpperCase() ||
                         roleIdStr.toUpperCase() === codeStr.toUpperCase();
                }
              );
              if (matchingRole) {
                // Use the id from the matching role to ensure it matches Select2Dropdown's getOptionValue
                userRoleValue = matchingRole.id || userRoleCode;
              }
            }
            
            return {
              id: `row-${Date.now()}-${index}`,
              approvalLevelCode: level.approvalLevelCode || level.ApprovalLevelCode || "",
              levelSequence: level.levelSequence || level.LevelSequence || index + 1,
              approvalType: level.approvalType || level.ApprovalType || "Required",
              userRole: userRoleValue, // Use the code string that matches the option id
              minValue: level.minValue || level.MinValue || "",
              maxValue: level.maxValue || level.MaxValue || "",
              currency: level.currency || level.Currency || "INR",
              plantCode: level.plantcode || level.plantCode || level.PLANTCODE || "",
              isPlantQCRequired: level.isPlantQCRequired !== undefined 
                ? level.isPlantQCRequired 
                : level.IsPlantQCRequired !== undefined 
                ? level.IsPlantQCRequired 
                : false,
              isActive: level.isActive !== undefined 
                ? level.isActive 
                : level.IsActive !== undefined 
                ? level.IsActive 
                : true,
            };
          });
          
          // Now validate all rows with the complete row set for proper overlap checking
          const validatedRows = mappedRows.map((row, index) => {
            const validation = validateRowRange(
              row.id,
              row.minValue,
              row.maxValue,
              mappedRows
            );
            return {
              ...row,
              rangeError: validation.error,
              rangeValid: validation.isValid,
            };
          });
          
          setRows(validatedRows.length > 0 ? validatedRows : [createDefaultRow()]);
        };
        
        // Always map rows - if userRoleOptions aren't loaded yet, 
        // the useEffect hook will update them when roles become available
        mapRowsWithRoles();
      }
    } catch (error) {
      console.error("Error loading workflow levels:", error);
    }
  };

  const handleSaveWorkflow = async () => {
    // Validation
    if (!workflowForm.workflowName.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Workflow Name is required",
      });
      return;
    }

    if (!workflowForm.marketCodes?.length) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Market is required",
      });
      return;
    }

    if (!workflowForm.catgCodes?.length) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "At least one Business Vertical is required",
      });
      return;
    }

    if (pageType === "ApprovalValueConfiguration") {
      const spec = String(workflowForm.userSpecificationType || "").trim();
      const allowed = getUserSpecificationOptionsForMarkets(workflowForm.marketCodes, markets);
      if (!spec || spec === "None" || !allowed.some((o) => o.id === spec)) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "SR Journey Flow is required",
        });
        return;
      }
    }

    // In add mode, workflowCode is auto-generated, so try to load it if not present
    // If it fails to load, proceed anyway as backend can generate it
    if (!editingWorkflow && !workflowForm.workflowCode.trim()) {
      try {
        const response = await approvalWorkflowAPI.getNextCode();
        if (response?.success && response.data) {
          setWorkflowForm((prev) => ({ ...prev, workflowCode: response.data }));
        }
      } catch (error) {
        console.warn("Error loading next workflow code, proceeding with save:", error);
        // Continue with save - backend will generate the code if needed
      }
    }

    try {
      // Join categories array with commas for database storage
      const catgCodeString = workflowForm.catgCodes && workflowForm.catgCodes.length > 0 
        ? workflowForm.catgCodes.join(',') 
        : null;

      // Ensure pageType is set from URL or state - get from current URL to ensure accuracy
      const currentSearchParams = new URLSearchParams(location.search);
      const currentPageType = workflowForm.pageType || pageType || currentSearchParams.get('pageType') || 'ApprovalValueConfiguration';
      
      let srTypeCodeString = null;
      if (workflowForm.srTypeCode) {
        if (Array.isArray(workflowForm.srTypeCode)) {
          srTypeCodeString =
            workflowForm.srTypeCode.length > 0 ? workflowForm.srTypeCode.join(",") : null;
        } else if (typeof workflowForm.srTypeCode === "string" && workflowForm.srTypeCode.trim() !== "") {
          srTypeCodeString = workflowForm.srTypeCode.trim();
        }
      }
      if (
        currentPageType === "ApprovalValueConfiguration" &&
        srTypeCodeString &&
        srTypeCodeString.includes(",")
      ) {
        srTypeCodeString = srTypeCodeString.split(",")[0].trim();
      }

      const marketForApi = workflowForm.marketCodes?.length
        ? [...workflowForm.marketCodes].filter(Boolean).join(",")
        : null;

      const workflowData = {
        workflowCode: workflowForm.workflowCode,
        workflowName: workflowForm.workflowName,
        SRTYPECODE: srTypeCodeString,
        MARKETCODE: marketForApi,
        CATGCODE: catgCodeString,
        PageType: currentPageType,
        isActive: workflowForm.isActive,
        workflowSource: JSON.stringify({
          userSpecificationType: workflowForm.userSpecificationType || "None",
        }),
      };

      let response;
      if (editingWorkflow) {
        response = await approvalWorkflowAPI.update(workflowForm.workflowCode, workflowData);
      } else {
        response = await approvalWorkflowAPI.create(workflowData);
      }

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: editingWorkflow ? "Workflow updated successfully" : "Workflow created successfully",
        });
        setShowWorkflowForm(false);
        setEditingWorkflow(null);
        // Don't reset form if we're in edit mode - keep the data
        if (!isEditMode) {
          setWorkflowForm({
            workflowCode: "",
            workflowName: "",
            srTypeCode: "",
            marketCode: "0000",
            marketCodes: [],
            catgCodes: [],
            userSpecificationType: "None",
            workflowSource: "",
            pageType: pageType || initialPageType,
            isActive: true,
          });
          setBusinessVerticals([]);
        }
        if (isAddMode || isEditMode) {
          // Stay on the same page after save
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to save workflow",
        });
      }
    } catch (error) {
      console.error("Error saving workflow:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save workflow",
      });
    }
  };

  /**
   * Create a default row with empty/default values
   */
  const createDefaultRow = () => {
    return {
      id: `row-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      approvalLevelCode: "",
      levelSequence: 1,
      approvalType: "Required",
      userRole: null,
      minValue: "",
      maxValue: "",
      currency: "INR",
      plantCode: "",
      isPlantQCRequired: false,
      isActive: true,
    };
  };

  /**
   * Load user roles from API or use mock data
   */
  const loadUserRoles = async () => {
    try {
      setLoading(true);
      // Try to load from API
      try {
        const response = await masterDataAPI.getUserTypes();
        if (response?.success && response.data) {
          setUserRoleOptions(
            response.data.map((role) => ({
              id: role.userTypeCode || role.UserTypeCode,
              name: role.userTypeName || role.UserTypeName,
            }))
          );
        } else {
          // Fallback to mock data
          setUserRoleOptions(getMockUserRoles());
        }
      } catch (error) {
        console.warn("Failed to load user roles from API, using mock data:", error);
        setUserRoleOptions(getMockUserRoles());
      }
    } catch (error) {
      console.error("Error loading user roles:", error);
      setUserRoleOptions(getMockUserRoles());
    } finally {
      setLoading(false);
    }
  };

  /**
   * Mock user roles data (reusable and configurable)
   */
  const getMockUserRoles = () => {
    return [
      { id: "ADMIN", name: "Administrator" },
      { id: "APPROVER", name: "Approver" },
      { id: "SE", name: "Sales Executive" },
      { id: "PLANT_QC", name: "Plant QC" },
      { id: "MANAGER", name: "Manager" },
      { id: "SUPERVISOR", name: "Supervisor" },
    ];
  };

  /**
   * Check if a role already exists in workflows of the opposite pageType
   */
  const checkRoleExistsInOppositePageType = async (roleCode) => {
    if (!roleCode || roleCode === '' || roleCode === '0000') {
      return { exists: false, workflowName: null };
    }

    try {
      // Determine the opposite pageType
      const oppositePageType = pageType === 'ApprovalConfiguration' 
        ? 'ApprovalValueConfiguration' 
        : 'ApprovalConfiguration';

      // Get all workflows of the opposite pageType
      const workflowsResponse = await approvalWorkflowAPI.getAll({ isActive: true });
      
      if (!workflowsResponse?.success || !workflowsResponse.data) {
        return { exists: false, workflowName: null };
      }

      const workflows = Array.isArray(workflowsResponse.data) 
        ? workflowsResponse.data 
        : (workflowsResponse.data.workflows || []);

      // Filter workflows by opposite pageType
      const oppositeWorkflows = workflows.filter(w => 
        (w.pageType || w.PageType) === oppositePageType
      );

      // Check each workflow's levels for the role
      for (const workflow of oppositeWorkflows) {
        const workflowCode = workflow.workflowCode || workflow.WorkflowCode;
        if (!workflowCode) continue;

        try {
          const levelsResponse = await approvalWorkflowAPI.getLevels(workflowCode);
          if (levelsResponse?.success && levelsResponse.data) {
            const levels = Array.isArray(levelsResponse.data) 
              ? levelsResponse.data 
              : (levelsResponse.data.levels || []);

            // Check if role exists in any level
            const roleExists = levels.some(level => {
              const levelRoleCode = level.userTypeCode || level.UserTypeCode || 
                                   level.userRole || level.UserRole ||
                                   level.roleCode || level.RoleCode;
              return String(levelRoleCode).trim() === String(roleCode).trim();
            });

            if (roleExists) {
              return { 
                exists: true, 
                workflowName: workflow.workflowName || workflow.WorkflowName || workflowCode 
              };
            }
          }
        } catch (error) {
          console.warn(`Error checking levels for workflow ${workflowCode}:`, error);
          // Continue checking other workflows
        }
      }

      return { exists: false, workflowName: null };
    } catch (error) {
      console.error('Error checking role in opposite pageType:', error);
      // Return false on error to allow the operation to proceed
      // This prevents blocking users if there's a network issue
      return { exists: false, workflowName: null };
    }
  };

  /**
   * Validate min/max values for a specific row
   */
  const validateRowRange = (rowId, minValue, maxValue, allRows) => {
    const currentRow = allRows.find(r => r.id === rowId);
    if (!currentRow) return { isValid: true, error: null };
    
    const rowIndex = allRows.findIndex(r => r.id === rowId);
    const errors = [];
    
    // Check if both values are provided
    if (minValue && maxValue) {
      const minValueStr = String(minValue).trim();
      const maxValueStr = String(maxValue).trim();
      
      // Validate integers
      if (!/^-?\d+$/.test(minValueStr)) {
        return { isValid: false, error: "Min Value must be an integer" };
      }
      
      if (!/^-?\d+$/.test(maxValueStr)) {
        return { isValid: false, error: "Max Value must be an integer" };
      }
      
      const min = parseInt(minValueStr, 10);
      const max = parseInt(maxValueStr, 10);
      
      // Check max > min
      if (min >= max) {
        return { isValid: false, error: "Max Value must be greater than Min Value" };
      }
      
      // Check sequential ordering first (more specific errors)
      // Check previous row's max should be < current min
      if (rowIndex > 0) {
        const prevRow = allRows[rowIndex - 1];
        if (prevRow.minValue && prevRow.maxValue) {
          const prevMinStr = String(prevRow.minValue).trim();
          const prevMaxStr = String(prevRow.maxValue).trim();
          if (/^-?\d+$/.test(prevMinStr) && /^-?\d+$/.test(prevMaxStr)) {
            const prevMin = parseInt(prevMinStr, 10);
            const prevMax = parseInt(prevMaxStr, 10);
            if (min <= prevMax) {
              return { 
                isValid: false, 
                error: `Min (${min}) must be > previous row's Max (${prevMax})` 
              };
            }
          }
        }
      }
      
      // Check sequential ordering (next row's min should be > current max)
      if (rowIndex < allRows.length - 1) {
        const nextRow = allRows[rowIndex + 1];
        if (nextRow.minValue && nextRow.maxValue) {
          const nextMinStr = String(nextRow.minValue).trim();
          const nextMaxStr = String(nextRow.maxValue).trim();
          if (/^-?\d+$/.test(nextMinStr) && /^-?\d+$/.test(nextMaxStr)) {
            const nextMin = parseInt(nextMinStr, 10);
            const nextMax = parseInt(nextMaxStr, 10);
            if (max >= nextMin) {
              return { 
                isValid: false, 
                error: `Max (${max}) must be < next row's Min (${nextMin})` 
              };
            }
          } else if (/^-?\d+$/.test(nextMinStr)) {
            const nextMin = parseInt(nextMinStr, 10);
            if (max >= nextMin) {
              return { 
                isValid: false, 
                error: `Max (${max}) must be < next row's Min (${nextMin})` 
              };
            }
          }
        }
      }
      
      // Check for overlaps with other rows (non-adjacent rows)
      const otherRows = allRows.filter((r, idx) => {
        // Exclude current row and adjacent rows (already checked above)
        return idx !== rowIndex && 
               idx !== rowIndex - 1 && 
               idx !== rowIndex + 1 && 
               r.minValue !== null && 
               r.minValue !== undefined && 
               r.minValue !== '' &&
               r.maxValue !== null && 
               r.maxValue !== undefined && 
               r.maxValue !== '';
      });
      
      for (const otherRow of otherRows) {
        const otherMinStr = String(otherRow.minValue).trim();
        const otherMaxStr = String(otherRow.maxValue).trim();
        
        if (/^-?\d+$/.test(otherMinStr) && /^-?\d+$/.test(otherMaxStr)) {
          const otherMin = parseInt(otherMinStr, 10);
          const otherMax = parseInt(otherMaxStr, 10);
          
          // Validate the other row's range first
          if (otherMin >= otherMax) {
            continue; // Skip invalid ranges
          }
          
          // Check for overlap
          if ((min <= otherMax && max >= otherMin)) {
            const otherIndex = allRows.findIndex(r => r.id === otherRow.id);
            if (otherIndex !== -1) {
              return { 
                isValid: false, 
                error: `Overlaps with Row ${otherIndex + 1} (${otherMin}-${otherMax})` 
              };
            }
          }
        }
      }
    } else if (minValue || maxValue) {
      // Only one value provided
      return { isValid: false, error: "Both Min and Max values are required" };
    }
    
    return { isValid: true, error: null };
  };

  /**
   * Handle field change for a specific row
   */
  const handleRowChange = async (rowId, field, value) => {
    // If role is being changed, validate it doesn't exist in opposite pageType or in other rows
    if (field === 'userRole' && value) {
      const roleCode = typeof value === 'string' 
        ? value 
        : (value?.id || value?.userTypeCode || value);
      
      if (roleCode && roleCode !== '' && roleCode !== '0000') {
        // First check if this role is already selected in another row within the same page
        const duplicateRow = rows.find(row => {
          if (row.id === rowId || !row.userRole) return false;
          const existingRoleCode = typeof row.userRole === 'string' 
            ? row.userRole 
            : (row.userRole?.id || row.userRole?.userTypeCode || row.userRole);
          return String(existingRoleCode).trim() === String(roleCode).trim();
        });

        if (duplicateRow) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "This role is already selected in another row. Please select a different role or remove it from the other row first.",
          });
          return; // Don't update the row
        }

        // Then check if it exists in opposite pageType
        const checkResult = await checkRoleExistsInOppositePageType(roleCode);
        if (checkResult.exists) {
          const oppositePageType = pageType === 'ApprovalConfiguration' 
            ? 'Approval Value Configuration' 
            : 'Approval Configuration';
          
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `This role already exists in ${oppositePageType} (Workflow: ${checkResult.workflowName}). The same role cannot be used in both page types.`,
          });
          return; // Don't update the row
        }
      }
    }

    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      );
      
      // Real-time validation for min/max values
      if (field === 'minValue' || field === 'maxValue') {
        const updatedRow = updatedRows.find(r => r.id === rowId);
        if (updatedRow) {
          const validation = validateRowRange(
            rowId, 
            updatedRow.minValue, 
            updatedRow.maxValue, 
            updatedRows
          );
          // Store validation error in row state
          updatedRow.rangeError = validation.error;
          updatedRow.rangeValid = validation.isValid;
        }
      }
      
      return updatedRows;
    });
  };

  /**
   * Check if a row has valid Min and Max values for adding a new row
   */
  const isRowValidForInsert = (row) => {
    const minValue = row.minValue;
    const maxValue = row.maxValue;
    
    // Check if values are empty
    if (!minValue || minValue === '' || minValue === null || minValue === undefined) {
      return false;
    }
    
    if (!maxValue || maxValue === '' || maxValue === null || maxValue === undefined) {
      return false;
    }
    
    // Convert to numbers
    const minNum = Number(minValue);
    const maxNum = Number(maxValue);
    
    // Check if conversion resulted in NaN
    if (isNaN(minNum) || isNaN(maxNum)) {
      return false;
    }
    
    // Check if values are 0 or less
    if (minNum <= 0 || maxNum <= 0) {
      return false;
    }
    
    // Check if max <= min
    if (maxNum <= minNum) {
      return false;
    }
    
    return true;
  };

  /**
   * Insert a new row at the specified index
   */
  const handleInsertRow = async (currentIndex) => {
    // Validate that the current row has a User Role selected
    const currentRow = rows[currentIndex];
    if (!currentRow) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: `Row ${currentIndex + 1} not found.`,
      });
      return;
    }
    
    // Validate Min and Max values before allowing insert
    if (!isRowValidForInsert(currentRow)) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please enter valid Min and Max Value before adding level",
      });
      return;
    }
    
    // Check if userRole is set - validate that a role is selected
    const userRoleValue = currentRow.userRole;
    let hasUserRole = false;
    let roleCode = null;
    
    // Explicitly check for valid userRole value
    if (userRoleValue !== null && userRoleValue !== undefined && userRoleValue !== '') {
      if (typeof userRoleValue === 'string') {
        hasUserRole = userRoleValue.trim() !== '' && userRoleValue.trim() !== '0000';
        roleCode = userRoleValue.trim();
      } else if (typeof userRoleValue === 'object') {
        const roleId = userRoleValue.id || userRoleValue.userTypeCode;
        hasUserRole = !!(roleId && roleId !== '' && roleId !== '0000' && roleId !== null && roleId !== undefined);
        roleCode = roleId;
      } else if (typeof userRoleValue === 'number') {
        hasUserRole = userRoleValue !== 0;
        roleCode = String(userRoleValue);
      } else {
        hasUserRole = true; // Other truthy values
      }
    }
    
    if (!hasUserRole) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: `Please select a User Role for row ${currentIndex + 1} before adding a new row.`,
      });
      return;
    }

    // Check if role exists in opposite pageType
    if (roleCode) {
      const checkResult = await checkRoleExistsInOppositePageType(roleCode);
      if (checkResult.exists) {
        const oppositePageType = pageType === 'ApprovalConfiguration' 
          ? 'Approval Value Configuration' 
          : 'Approval Configuration';
        
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: `This role already exists in ${oppositePageType} (Workflow: ${checkResult.workflowName}). The same role cannot be used in both page types.`,
        });
        return; // Don't insert the row
      }
    }

    const newRow = createDefaultRow();
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows.splice(currentIndex + 1, 0, newRow);
      // Update level sequences based on new positions
      const updatedRows = newRows.map((row, idx) => ({
        ...row,
        levelSequence: idx + 1
      }));
      
      // Validate all rows after insertion to check sequential ordering
      const validatedRows = validateAllRows(updatedRows);
      
      return validatedRows;
    });
  };

  /**
   * Delete a row by ID
   */
  const handleDeleteRow = (rowId) => {
    if (rows.length <= 1) {
      // Prevent deleting the last row
      return;
    }
    setRows((prevRows) => {
      const filtered = prevRows.filter((row) => row.id !== rowId);
      // Update level sequences after deletion
      const updatedRows = filtered.map((row, idx) => ({
        ...row,
        levelSequence: idx + 1
      }));
      
      // Validate all rows after deletion to check sequential ordering
      const validatedRows = validateAllRows(updatedRows);
      
      return validatedRows;
    });
    
    // Clean up Select2 instance if it exists
    if (select2Refs.current[rowId]) {
      const $select = $(select2Refs.current[rowId]);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      delete select2Refs.current[rowId];
    }
  };

  /**
   * Drag and Drop Handlers
   */
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
    e.target.style.opacity = "0.5";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  /**
   * Validate all rows after reordering
   */
  const validateAllRows = (allRows) => {
    return allRows.map((row) => {
      const validation = validateRowRange(
        row.id,
        row.minValue,
        row.maxValue,
        allRows
      );
      return {
        ...row,
        rangeError: validation.error,
        rangeValid: validation.isValid
      };
    });
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setRows((prevRows) => {
      const newRows = [...prevRows];
      const draggedRow = newRows[draggedIndex];
      
      // Remove dragged item
      newRows.splice(draggedIndex, 1);
      
      // Insert at new position
      const newIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      newRows.splice(newIndex, 0, draggedRow);
      
      // Update level sequences based on new positions
      const reorderedRows = newRows.map((row, idx) => ({
        ...row,
        levelSequence: idx + 1
      }));
      
      // Validate all rows after reordering to check sequential ordering
      const validatedRows = validateAllRows(reorderedRows);
      
      return validatedRows;
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  /**
   * Load options for Select2 dropdown (async)
   * @param {string} searchText - Search text to filter roles
   * @param {number} pageNumber - Page number for pagination
   * @param {number} pageSize - Number of items per page
   * @param {string} currentRowId - ID of the current row (to exclude its selected role from filtering)
   */
  const loadRoleOptions = async (searchText, pageNumber, pageSize, currentRowId = null) => {
    // Get roles already selected in other rows (excluding current row)
    const selectedRoleIds = rows
      .filter(row => row.id !== currentRowId && row.userRole) // Exclude current row and rows without role
      .map(row => {
        const roleValue = row.userRole;
        if (typeof roleValue === 'string') {
          return roleValue.trim();
        } else if (typeof roleValue === 'object' && roleValue !== null) {
          return roleValue.id || roleValue.userTypeCode || String(roleValue);
        } else if (typeof roleValue === 'number') {
          return String(roleValue);
        }
        return null;
      })
      .filter(id => id && id !== '' && id !== '0000');

    // Filter options based on search text and exclude already selected roles
    const filtered = userRoleOptions.filter((role) => {
      const roleId = String(role.id || role.userTypeCode || '');
      const matchesSearch = role.name.toLowerCase().includes(searchText.toLowerCase());
      const notSelected = !selectedRoleIds.includes(roleId);
      return matchesSearch && notSelected;
    });

    // Simulate pagination
    const start = (pageNumber - 1) * pageSize;
    const end = start + pageSize;
    const paginated = filtered.slice(start, end);

    return {
      data: paginated,
      totalCount: filtered.length,
    };
  };

  /**
   * Handle combined save action - saves both workflow and levels
   */
  const handleSave = async () => {
    // Validate workflow form
    if (!workflowForm.workflowName.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Workflow Name is required",
      });
      return;
    }

    // In add mode, workflowCode is auto-generated, so ensure it's loaded before saving
    let finalWorkflowCode = workflowForm.workflowCode;
    if (!editingWorkflow && !finalWorkflowCode.trim()) {
      try {
        const response = await approvalWorkflowAPI.getNextCode();
        if (response?.success && response.data) {
          finalWorkflowCode = response.data;
          setWorkflowForm((prev) => ({ ...prev, workflowCode: finalWorkflowCode }));
        } else {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Could not generate workflow code. Please try again.",
          });
          return;
        }
      } catch (error) {
        console.error("Error loading next workflow code:", error);
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Could not generate workflow code. Please try again.",
        });
        return;
      }
    }

    // Ensure workflowCode is not empty before proceeding
    if (!finalWorkflowCode || !finalWorkflowCode.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Workflow Code is required",
      });
      return;
    }

    if (!workflowForm.marketCodes || workflowForm.marketCodes.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Market is required",
      });
      return;
    }

    if (!workflowForm.catgCodes || workflowForm.catgCodes.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "At least one Business Vertical is required",
      });
      return;
    }

    if (pageType === "ApprovalValueConfiguration") {
      const spec = String(workflowForm.userSpecificationType || "").trim();
      const allowed = getUserSpecificationOptionsForMarkets(workflowForm.marketCodes, markets);
      if (!spec || spec === "None" || !allowed.some((o) => o.id === spec)) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "SR Journey Flow is required",
        });
        return;
      }
    }

    // Validate rows
    if (rows.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "At least one approval level is required",
      });
      return;
    }

    // Validate each row
    const rowRanges = []; // Store valid ranges for overlap checking
    
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      
      // Validate User Role is selected
      const userRoleValue = row.userRole;
      let hasUserRole = false;
      
      if (userRoleValue !== null && userRoleValue !== undefined && userRoleValue !== '') {
        if (typeof userRoleValue === 'string') {
          hasUserRole = userRoleValue.trim() !== '' && userRoleValue.trim() !== '0000';
        } else if (typeof userRoleValue === 'object') {
          const roleId = userRoleValue.id || userRoleValue.userTypeCode;
          hasUserRole = !!(roleId && roleId !== '' && roleId !== '0000' && roleId !== null && roleId !== undefined);
        } else if (typeof userRoleValue === 'number') {
          hasUserRole = userRoleValue !== 0;
        } else {
          hasUserRole = true; // Other truthy values
        }
      }
      
      if (!hasUserRole) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: `Please select a User Role for row ${i + 1} before saving.`,
        });
        return;
      }

      // Check if role exists in opposite pageType
      let roleCode = null;
      if (userRoleValue !== null && userRoleValue !== undefined && userRoleValue !== '') {
        if (typeof userRoleValue === 'string') {
          roleCode = userRoleValue.trim();
        } else if (typeof userRoleValue === 'object') {
          roleCode = userRoleValue.id || userRoleValue.userTypeCode;
        } else if (typeof userRoleValue === 'number') {
          roleCode = String(userRoleValue);
        }
      }

      if (roleCode && roleCode !== '' && roleCode !== '0000') {
        const checkResult = await checkRoleExistsInOppositePageType(roleCode);
        if (checkResult.exists) {
          const oppositePageType = pageType === 'ApprovalConfiguration' 
            ? 'Approval Value Configuration' 
            : 'Approval Configuration';
          
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Row ${i + 1}: This role already exists in ${oppositePageType} (Workflow: ${checkResult.workflowName}). The same role cannot be used in both page types.`,
          });
          return;
        }
      }
      
      // Validate MinValue and MaxValue range
      if (row.minValue && row.maxValue) {
        // Check if values are valid integers
        const minValueStr = String(row.minValue).trim();
        const maxValueStr = String(row.maxValue).trim();
        
        // Validate that values are integers (not decimals)
        if (!/^-?\d+$/.test(minValueStr)) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `In row ${i + 1}: Min Value must be an integer (whole number). Current value: ${minValueStr}`,
          });
          return;
        }
        
        if (!/^-?\d+$/.test(maxValueStr)) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `In row ${i + 1}: Max Value must be an integer (whole number). Current value: ${maxValueStr}`,
          });
          return;
        }
        
        const minValue = parseInt(minValueStr, 10);
        const maxValue = parseInt(maxValueStr, 10);
        
        if (!isNaN(minValue) && !isNaN(maxValue)) {
          // First validation: Max value must be greater than Min value
          if (minValue >= maxValue) {
            Swal.fire({
              icon: "error",
              title: "Validation Error",
              text: `In row ${i + 1}: Max Value must be greater than Min Value. Current values: Min=${minValue}, Max=${maxValue}`,
            });
            return;
          }
          
          // Store this range for overlap checking
          rowRanges.push({
            rowIndex: i,
            min: minValue,
            max: maxValue,
          });
        }
      } else if (row.minValue || row.maxValue) {
        // If only one value is provided, show error
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: `In row ${i + 1}: Both Min Value and Max Value are required for range validation.`,
        });
        return;
      }
    }
    
    // Validate range overlaps and sequential ordering
    if (rowRanges.length > 1) {
      // Check ranges in row order (not sorted) to ensure sequential progression
      // Sort by row index to maintain row order
      const orderedRanges = [...rowRanges].sort((a, b) => a.rowIndex - b.rowIndex);
      
      // Check for overlaps and ensure sequential ordering
      for (let i = 0; i < orderedRanges.length - 1; i++) {
        const currentRange = orderedRanges[i];
        const nextRange = orderedRanges[i + 1];
        
        // Check if ranges overlap (current max should be < next min for sequential ranges)
        if (currentRange.max >= nextRange.min) {
          const currentRowIndex = currentRange.rowIndex + 1;
          const nextRowIndex = nextRange.rowIndex + 1;
          
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Range overlap detected! Row ${currentRowIndex} (${currentRange.min}-${currentRange.max}) overlaps with Row ${nextRowIndex} (${nextRange.min}-${nextRange.max}). Ranges must be sequential and non-overlapping. The next row's Min Value should be greater than the previous row's Max Value.`,
          });
          return;
        }
      }
      
      // Also check all pairs for any overlaps (in case rows are not in order)
      for (let i = 0; i < rowRanges.length; i++) {
        for (let j = i + 1; j < rowRanges.length; j++) {
          const range1 = rowRanges[i];
          const range2 = rowRanges[j];
          
          // Check if ranges overlap (not just sequential)
          if (
            (range1.min <= range2.max && range1.max >= range2.min) ||
            (range2.min <= range1.max && range2.max >= range1.min)
          ) {
            const row1Index = range1.rowIndex + 1;
            const row2Index = range2.rowIndex + 1;
            
            // Only show error if it's not already caught by sequential check
            if (Math.abs(range1.rowIndex - range2.rowIndex) > 1) {
              Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: `Range overlap detected! Row ${row1Index} (${range1.min}-${range1.max}) overlaps with Row ${row2Index} (${range2.min}-${range2.max}). Ranges must be non-overlapping.`,
              });
              return;
            }
          }
        }
      }
    }

    try {
      setSaving(true);

      // Join categories array with commas for database storage
      const catgCodeString = workflowForm.catgCodes && workflowForm.catgCodes.length > 0 
        ? workflowForm.catgCodes.join(',') 
        : null;

      // Join SR Type codes array with commas for database storage (if it's an array)
      let srTypeCodeString = null;
      if (workflowForm.srTypeCode) {
        if (Array.isArray(workflowForm.srTypeCode)) {
          srTypeCodeString = workflowForm.srTypeCode.length > 0 
            ? workflowForm.srTypeCode.join(',') 
            : null;
        } else if (typeof workflowForm.srTypeCode === 'string' && workflowForm.srTypeCode.trim() !== '') {
          srTypeCodeString = workflowForm.srTypeCode.trim();
        }
      }
      if (
        (workflowForm.pageType || pageType) === "ApprovalValueConfiguration" &&
        srTypeCodeString &&
        srTypeCodeString.includes(",")
      ) {
        srTypeCodeString = srTypeCodeString.split(",")[0].trim();
      }

      // Ensure pageType is set from URL or state - get from current URL to ensure accuracy
      const currentSearchParams = new URLSearchParams(location.search);
      const currentPageType = workflowForm.pageType || pageType || currentSearchParams.get('pageType') || 'ApprovalValueConfiguration';
      
      const marketForApi = workflowForm.marketCodes?.length
        ? [...workflowForm.marketCodes].filter(Boolean).join(",")
        : null;

      // Prepare workflow data - use finalWorkflowCode to ensure it's set
      const workflowData = {
        workflowCode: finalWorkflowCode || workflowForm.workflowCode,
        workflowName: workflowForm.workflowName,
        SRTYPECODE: srTypeCodeString, // Can be comma-separated string or null
        MARKETCODE: marketForApi,
        CATGCODE: catgCodeString,
        PageType: currentPageType, // Use PageType (PascalCase) to match database column name
        isActive: workflowForm.isActive,
        workflowSource: JSON.stringify({
          userSpecificationType: workflowForm.userSpecificationType || "None",
        }),
      };

      // Prepare levels data - use finalWorkflowCode to ensure it's set
      // Sequence is maintained internally based on row order
      const currentWorkflowCode = finalWorkflowCode || workflowForm.workflowCode;
      const levelsData = rows.map((row, index) => ({
        approvalLevelCode: row.approvalLevelCode || `${currentWorkflowCode}_LVL${String(index + 1).padStart(3, '0')}`,
        workflowCode: currentWorkflowCode,
        levelSequence: index + 1, // Maintained internally based on row order
        approvalType: (row.approvalType && row.approvalType.trim()) ? row.approvalType : "Required", // Default value maintained internally - always send "Required" if not set
        minValue: row.minValue ? parseInt(String(row.minValue).trim(), 10) : null,
        maxValue: row.maxValue ? parseInt(String(row.maxValue).trim(), 10) : null,
        currency: row.currency || null,
        PLANTCODE: row.plantCode || null, // Default value maintained internally
        USERTYPECODE: row.userRole?.id || row.userRole || null,
        isPlantQCRequired: row.isPlantQCRequired || false, // Default value maintained internally
        isActive: row.isActive !== undefined ? row.isActive : true,
      }));

      // Call combined save API
      const response = await approvalWorkflowAPI.saveWorkflowWithLevels(workflowData, levelsData, editingWorkflow ? "update" : "create");

      if (response?.success) {
        const savedWorkflowCode = response.data?.workflowCode || workflowData.workflowCode;
        
        // Get current pageType from query string to ensure accuracy
        const currentSearchParams = new URLSearchParams(location.search);
        const currentPageType = currentSearchParams.get('pageType') || workflowForm.pageType || pageType || 'ApprovalValueConfiguration';
        
        Swal.fire({
          icon: "success",
          title: "Success",
          text: editingWorkflow ? "Workflow and levels updated successfully" : "Workflow and levels created successfully",
          confirmButtonText: "OK",
        }).then(() => {
          // Navigate to management page - both pageTypes use the same management page
          navigate("/dynamic-approval-configuration");
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to save workflow and levels",
        });
      }
    } catch (error) {
      console.error("Error saving workflow and levels:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to save workflow and levels",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading && userRoleOptions.length === 0) {
    return (
      <Box className="approval-level-setup-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="approval-level-setup-container">
      {/* Header Section */}
      <Box className="approval-level-setup-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="approval-level-setup-breadcrumb"
        >
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            Dynamic Approval Value Configuration
          </Typography>
        </Breadcrumbs>
        <Box className="approval-level-setup-header-right">
          <Button
            className="approval-level-setup-back-button"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <div className="container-fluid approval-level-setup-page">
        {/* Workflow Form Section - Upper Panel */}
        <div className="card-panel mb-3">
          <div className="section-heading d-flex align-items-center justify-content-between mb-3">
            <Typography variant="h6" className="add-edit-user-section-heading">
              Approval Workflows
            </Typography>
          </div>

          {showWorkflowForm && (
            <div className="row g-3 mb-4">
              <input type="hidden" value={workflowForm.workflowCode} />

              {pageType === "ApprovalValueConfiguration" && (
                <>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="select2-wrapper">
                      <label className="select2-label pnlLblsze">
                        Market <span style={{ color: "red" }}>*</span>
                      </label>
                      <CustomMultiSelect
                        options={(markets || [])
                          .map((m) => ({
                            id: m.marketCode || m.MARKETCODE || m.MarketCode || "",
                            text:
                              m.marketName ||
                              m.MARKETNAME ||
                              m.MarketName ||
                              m.marketCode ||
                              m.MARKETCODE ||
                              m.MarketCode ||
                              "",
                          }))
                          .filter((o) => o.id)}
                        value={
                          workflowForm.marketCodes?.length
                            ? [String(workflowForm.marketCodes[0]).trim()]
                            : []
                        }
                        onChange={(selected) => {
                          const picked =
                            Array.isArray(selected) && selected.length ? selected[0] : "";
                          const code = picked ? String(picked).trim() : "";
                          setWorkflowForm((prev) => ({
                            ...prev,
                            marketCodes: code ? [code] : [],
                            marketCode: code || "0000",
                            catgCodes: [],
                            srTypeCode: "",
                            userSpecificationType: "",
                          }));
                        }}
                        placeholder="Select Market"
                        disabled={false}
                        showSelectAll={false}
                        singleSelect
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="select2-wrapper">
                      <label className="select2-label pnlLblsze">
                        Business Vertical <span style={{ color: "red" }}>*</span>
                      </label>
                      <CustomMultiSelect
                        options={(businessVerticals || [])
                          .map((c) => ({
                            id: c.catgCode || c.CATGCODE || c.CatgCode || "",
                            text:
                              c.catgName ||
                              c.CATGNAME ||
                              c.CatgName ||
                              c.catgCode ||
                              c.CATGCODE ||
                              "",
                          }))
                          .filter((o) => o.id)}
                        value={workflowForm.catgCodes || []}
                        onChange={(selected) =>
                          setWorkflowForm((prev) => ({
                            ...prev,
                            catgCodes: Array.isArray(selected) ? selected : [],
                          }))
                        }
                        placeholder={
                          !workflowForm.marketCodes?.length
                            ? "Select Market first"
                            : "Select Business Vertical"
                        }
                        disabled={!workflowForm.marketCodes?.length}
                        showSelectAll
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="select2-wrapper">
                      <label className="select2-label pnlLblsze">SR Type</label>
                      <CustomMultiSelect
                        options={srTypeOptions || []}
                        value={(() => {
                          const v = workflowForm.srTypeCode;
                          if (v == null || v === "") return [];
                          const code = Array.isArray(v)
                            ? String(v[0] || "").trim()
                            : String(v).split(",")[0].trim();
                          return code ? [code] : [];
                        })()}
                        onChange={(selected) => {
                          const picked =
                            Array.isArray(selected) && selected.length ? selected[0] : "";
                          setWorkflowForm((prev) => ({
                            ...prev,
                            srTypeCode: picked ? String(picked).trim() : "",
                          }));
                        }}
                        placeholder={
                          !workflowForm.marketCodes?.length
                            ? "Select Market first"
                            : "Select SR Type"
                        }
                        disabled={!workflowForm.marketCodes?.length}
                        showSelectAll={false}
                        singleSelect
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="select2-wrapper">
                      <label className="select2-label pnlLblsze">
                        SR Journey Flow <span style={{ color: "red" }}>*</span>
                      </label>
                      <CustomMultiSelect
                        options={approvalValueUserSpecOptions}
                        value={
                          workflowForm.userSpecificationType &&
                          workflowForm.userSpecificationType !== "None"
                            ? [workflowForm.userSpecificationType]
                            : []
                        }
                        onChange={(selected) => {
                          const picked =
                            Array.isArray(selected) && selected.length ? selected[0] : "";
                          setWorkflowForm((prev) => ({
                            ...prev,
                            userSpecificationType: picked || "",
                          }));
                        }}
                        placeholder={
                          !workflowForm.marketCodes?.length
                            ? "Select Market first"
                            : "Select SR Journey Flow"
                        }
                        disabled={
                          !workflowForm.marketCodes?.length ||
                          !approvalValueUserSpecOptions.length
                        }
                        showSelectAll={false}
                        singleSelect
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="textfield-wrapper wrkflwPnl">
                      <label className="select2-label pnlLblsze">
                        Workflow Name <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        value={workflowForm.workflowName}
                        onChange={(e) =>
                          setWorkflowForm({ ...workflowForm, workflowName: e.target.value })
                        }
                        required
                        fullWidth
                        size="small"
                        placeholder="Enter Workflow Name"
                        className="add-edit-user-textfield"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
        </div>

        {/* Level Setup Section */}
        <div className="card-panel approval-level-panel">
          <Typography variant="h6" className="section-heading">
            Dynamic Approval Value Configuration Levels
          </Typography>

          {/* Rows Container */}
          <div className="approval-level-rows">
            {rows.map((row, index) => (
              <div
                key={row.id}
                className={`approval-level-row ${
                  dragOverIndex === index ? "drag-over" : ""
                } ${draggedIndex === index ? "dragging" : ""}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                {/* Drag Handle */}
                <div className="drag-handle">
                  <DragIndicatorIcon className="drag-icon" />
                </div>

                {/* Row Content */}
                <div className="row-content">
                  <div className="row g-3">
                    {/* User Role Select */}
                    <div className="col-12 col-md-3">
                      <div className="select2-wrapper">
                        <label className="select2-label pnlLblsze">
                          User Role
                        </label>
                        <Select2Dropdown
                          defaultOptions={userRoleOptions.filter((role) => {
                            // Filter out roles already selected in other rows (excluding current row)
                            const selectedRoleIds = rows
                              .filter(r => r.id !== row.id && r.userRole)
                              .map(r => {
                                const roleValue = r.userRole;
                                if (typeof roleValue === 'string') {
                                  return roleValue.trim();
                                } else if (typeof roleValue === 'object' && roleValue !== null) {
                                  return roleValue.id || roleValue.userTypeCode || String(roleValue);
                                } else if (typeof roleValue === 'number') {
                                  return String(roleValue);
                                }
                                return null;
                              })
                              .filter(id => id && id !== '' && id !== '0000');
                            const roleId = String(role.id || role.userTypeCode || '');
                            return !selectedRoleIds.includes(roleId);
                          })}
                          loadOptions={(searchText, pageNumber, pageSize) => 
                            loadRoleOptions(searchText, pageNumber, pageSize, row.id)
                          }
                          value={row.userRole}
                          onChange={(value) =>
                            handleRowChange(row.id, "userRole", value)
                          }
                          placeholder="Select User Role"
                          getOptionLabel={(option) => option?.name || String(option)}
                          getOptionValue={(option) => option?.id || option}
                          pageSize={20}
                          minSearchLength={0}
                          allowClear={true}
                        />
                      </div>
                    </div>

                    {/* Minimum Value Input */}
                    <div className="col-12 col-md-2">
                      <div className="textfield-wrapper">
                        <label className="select2-label pnlLblsze">
                          Min Value
                        </label>
                        <TextField
                          type="number"
                          value={row.minValue}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Remove any decimal point and everything after it
                            if (value.includes('.')) {
                              value = value.split('.')[0];
                            }
                            // Remove any non-digit characters except empty string
                            if (value !== '' && !/^-?\d+$/.test(value)) {
                              value = value.replace(/[^\d-]/g, '');
                            }
                            handleRowChange(row.id, "minValue", value);
                          }}
                          onBlur={() => {
                            // Trigger validation on blur
                            const validation = validateRowRange(
                              row.id,
                              row.minValue,
                              row.maxValue,
                              rows
                            );
                            setRows((prevRows) =>
                              prevRows.map((r) =>
                                r.id === row.id
                                  ? { ...r, rangeError: validation.error, rangeValid: validation.isValid }
                                  : r
                              )
                            );
                          }}
                          fullWidth
                          size="small"
                          placeholder="Min"
                          className="add-edit-user-textfield"
                          error={row.rangeError && !row.rangeValid}
                          helperText={row.rangeError && !row.rangeValid ? row.rangeError : ""}
                          inputProps={{ step: "1", min: "0" }}
                          onKeyDown={(e) => {
                            // Prevent decimal point and other non-integer characters
                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Maximum Value Input */}
                    <div className="col-12 col-md-2">
                      <div className="textfield-wrapper">
                        <label className="select2-label pnlLblsze">
                          Max Value
                        </label>
                        <TextField
                          type="number"
                          value={row.maxValue}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Remove any decimal point and everything after it
                            if (value.includes('.')) {
                              value = value.split('.')[0];
                            }
                            // Remove any non-digit characters except empty string
                            if (value !== '' && !/^-?\d+$/.test(value)) {
                              value = value.replace(/[^\d-]/g, '');
                            }
                            handleRowChange(row.id, "maxValue", value);
                          }}
                          onBlur={() => {
                            // Trigger validation on blur
                            const validation = validateRowRange(
                              row.id,
                              row.minValue,
                              row.maxValue,
                              rows
                            );
                            setRows((prevRows) =>
                              prevRows.map((r) =>
                                r.id === row.id
                                  ? { ...r, rangeError: validation.error, rangeValid: validation.isValid }
                                  : r
                              )
                            );
                          }}
                          fullWidth
                          size="small"
                          placeholder="Max"
                          className="add-edit-user-textfield"
                          error={row.rangeError && !row.rangeValid}
                          helperText={row.rangeError && !row.rangeValid ? row.rangeError : ""}
                          inputProps={{ step: "1", min: "0" }}
                          onKeyDown={(e) => {
                            // Prevent decimal point and other non-integer characters
                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Currency Input */}
                    <div className="col-12 col-md-2">
                      <div className="textfield-wrapper">
                        <label className="select2-label pnlLblsze">
                          Currency
                        </label>
                        <TextField
                          value={row.currency || "INR"}
                          onChange={(e) =>
                            handleRowChange(
                              row.id,
                              "currency",
                              e.target.value
                            )
                          }
                          fullWidth
                          size="small"
                          placeholder="INR"
                          className="add-edit-user-textfield"
                        />
                      </div>
                    </div>

                    {/* Active/Inactive Toggle */}
                    <div className="col-12 col-md-1">
                      <div className="toggle-wrapper">
                        <label className="select2-label pnlLblsze">
                          Status
                        </label>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={row.isActive}
                              onChange={(e) =>
                                handleRowChange(
                                  row.id,
                                  "isActive",
                                  e.target.checked
                                )
                              }
                              color="primary"
                            />
                          }
                          label={row.isActive ? "Active" : "Inactive"}
                          className="toggle-label"
                        />
                      </div>
                    </div>

                    {/* Row Actions */}
                    <div className="col-12 col-md-1">
                      <div className="row-actions">
                        <Button
                          className="row-action-btn add-btn"
                          onClick={() => handleInsertRow(index)}
                          disabled={!isRowValidForInsert(row)}
                          title={!isRowValidForInsert(row) ? "Please enter valid Min and Max Value before adding level" : "Insert row below"}
                          size="small"
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                        <Button
                          className="row-action-btn remove-btn"
                          onClick={() => handleDeleteRow(row.id)}
                          disabled={rows.length <= 1}
                          title="Delete row"
                          size="small"
                        >
                          <RemoveIcon fontSize="small" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info Message and Action Buttons */}
          <div className="approval-level-info-container">
            <div className="approval-level-info">
              <Typography variant="body2" className="info-text fw-bold">
                Drag rows to reorder • Click <span className="text-success">&#65291;</span> to insert a row below • Click <span className="text-danger"> &ndash; </span> to
                delete a row
              </Typography>
            </div>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/dynamic-approval-configuration")}
                className="btn-cancel-user funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                className="approval-level-save-button btn-save-user funbtns"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default DynamicApprovalValueConfigurationLevelSetup;

